import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { QUOTES_KEY, QUOTES_RESOURCE } from '../quotes/types';
import { Notice } from '../ui/Notice';
import { refreshCrew } from './refreshCrew';
import { crewShort, differences, quotePrompt, type PositionsCompare } from './positions';

const compareUrl = (eventId: string) => `/api/quotes/events/${eventId}/crew-positions`;
export const crewQuoteKey = (eventId: string) => [...QUOTES_KEY, 'crew-compare', eventId];

/**
 * Owner 18:07 („pytaj zamiast automatu”), 18:09 and 18:30: the crew never changes the quote by itself. Owner 22:02
 * „nie chcę, żeby wyskakiwało okienko … niech wyświetli się zielony pasek z pytaniem”: ONE green bar (`.notice`, as
 * „Sprzętówka różni się od kosztorysu”), never a dialog:
 * - while the crew and the quote differ and the user may change the quote and has not said „Nie” to this difference:
 *   the question „Technik +1, Realizator −1 — zaktualizować kosztorys?” with its answer button and „Nie” (a sent quote:
 *   „Zrobić nową wersję?”; an accepted one says so);
 * - after „Nie” (remembered for that difference), or for whoever may only see: „Obsada różni się od kosztorysu: …”
 *   with „Dodaj” / „Zdejmij” per function (when allowed).
 * Only for whoever may see the quote. No quote: nothing. `ask()` (after a crew change) refreshes the comparison.
 * Owner 2026-10-27 21:02: the bar sits at the bottom of the page with „×” — „not now”: it only hides the bar (the quote
 * and the „Nie” memory are untouched). Review M6: „not now” is per event, for every screen showing its bar; the next
 * crew change of the event — `ask()` from any screen, or a new comparison however it came — shows it again. Review M1:
 * two screens of one event (the grid's panel and its fill) show one bar.
 */
const rounds = new Map<string, number>();
const notNow = new Map<string, number>();
const seen = new Map<string, string>();
const listeners = new Set<() => void>();
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
function crewChanged(eventId: string) {
  rounds.set(eventId, (rounds.get(eventId) ?? 0) + 1);
  listeners.forEach((l) => l());
}
function closeForNow(eventId: string) {
  notNow.set(eventId, rounds.get(eventId) ?? 0);
  listeners.forEach((l) => l());
}
/** The event's crew-change round, and whether its bar is closed „for now” in it. */
function useNotNow(eventId: string): { round: number; closed: boolean } {
  const round = useSyncExternalStore(subscribe, () => rounds.get(eventId) ?? 0, () => 0);
  const closed = useSyncExternalStore(subscribe, () => notNow.get(eventId) === (rounds.get(eventId) ?? 0), () => false);
  return { round, closed };
}
export type CrewQuote = {
  ask: () => Promise<void>;
  /** The bar as a whole (Obsada, the grid's panel). */
  notice: ReactNode;
  /** Its parts, for the quote's own banner (18:30, 1): the bar's sentence and its buttons; '' when equal. */
  text: string;
  buttons: ReactNode;
  /** Kept for the callers: always null now (no dialog). */
  dialog: ReactNode;
  /**
   * Owner 2026-10-31: „Uzupełnij obsadę” under the given label (the quote's banner: „Uzupełnij obsadę z kosztorysu”) —
   * raises the places in Obsada where the quote has more; null when there is nothing to fill or no right.
   */
  fillCrew: (label: string) => ReactNode;
};

export function useCrewQuote(eventId: string): CrewQuote {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const { round, closed } = useNotNow(eventId);
  const seesQuote = !!me && can(me, QUOTES_RESOURCE, 'view');
  const compare = useQuery({
    queryKey: crewQuoteKey(eventId),
    enabled: seesQuote,
    retry: false,
    queryFn: () => api<PositionsCompare>(compareUrl(eventId)),
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
    await refreshCrew(queryClient);
  };
  // owner 2026-10-31: Obsada from the quote — only the places change, the quote stays
  const fill = useMutation({
    mutationFn: () => api<PositionsCompare>(`${compareUrl(eventId)}/fill-crew`, { method: 'POST', body: {} }),
    onSuccess: refresh,
  });
  const apply = useMutation({
    mutationFn: (b: { functionId?: string; newVersion?: boolean }) => api<PositionsCompare>(`${compareUrl(eventId)}/apply`, { method: 'POST', body: b }),
    onSuccess: async () => {
      fill.reset();
      await refresh();
    },
  });
  const dismiss = useMutation({
    mutationFn: (signature: string) => api(`${compareUrl(eventId)}/dismiss`, { method: 'POST', body: { signature } }),
    onSuccess: () => fill.reset(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: crewQuoteKey(eventId) }),
  });

  async function ask() {
    if (!seesQuote) return;
    crewChanged(eventId);
    await queryClient.invalidateQueries({ queryKey: crewQuoteKey(eventId) });
  }

  const c = compare.data;
  // a new comparison (a crew change made anywhere, the quote changed) — the bar may show again
  const compared = c ? JSON.stringify(c) : '';
  useEffect(() => {
    if (!compared || seen.get(eventId) === compared) return;
    const first = !seen.has(eventId);
    seen.set(eventId, compared);
    if (!first) crewChanged(eventId);
  }, [eventId, compared]);
  const diff = c?.quote ? differences(c) : [];
  const prompt = c ? quotePrompt(c) : null;
  const busy = apply.isPending || dismiss.isPending || fill.isPending;
  const short = c ? crewShort(c) : [];
  const fillCrew = (label: string): ReactNode =>
    short.length > 0 ? (
      <>
        <button className="btn small" type="button" disabled={busy} onClick={() => fill.mutate()}>
          {label}
        </button>
        {fill.error && <span role="alert">{fill.error instanceof ApiError ? fill.error.message : 'Nie udało się uzupełnić obsady'}</span>}
      </>
    ) : null;
  let text = '';
  let barButtons: ReactNode = null;
  let buttons: ReactNode = null;
  if (prompt) {
    text = prompt.newVersion ? `${prompt.text} ${prompt.title}` : prompt.text;
    const answer = (
      <button className="btn small primary" type="button" disabled={busy} onClick={() => apply.mutate(prompt.newVersion ? { newVersion: true } : {})}>
        {prompt.confirm}
      </button>
    );
    const no = (
      <button className="btn small" type="button" disabled={busy} onClick={() => dismiss.mutate(prompt.signature)}>
        Nie
      </button>
    );
    buttons = (
      <>
        {answer}
        {no}
      </>
    );
    // owner 2026-10-31: „Uzupełnij obsadę” between the answer and „Nie” (the quote's banner places its own)
    barButtons = (
      <>
        {answer}
        {fillCrew('Uzupełnij obsadę')}
        {no}
      </>
    );
  } else if (diff.length > 0) {
    text = `Obsada różni się od kosztorysu: ${diff.map((d) => d.text).join(', ')}.`;
    buttons = c?.mayApply
      ? diff.map((d) => (
          <button key={d.fn.functionId} className="btn small" type="button" disabled={busy} onClick={() => apply.mutate({ functionId: d.fn.functionId, ...(c.newVersion ? { newVersion: true } : {}) })}>
            {d.delta > 0 ? 'Dodaj' : 'Zdejmij'}: {d.fn.functionName}
            {c.newVersion ? ' (nowa wersja)' : ''}
          </button>
        ))
      : null;
    barButtons = (
      <>
        {buttons}
        {fillCrew('Uzupełnij obsadę')}
      </>
    );
  }
  const note = prompt?.note;
  const error = apply.error ? (apply.error instanceof ApiError ? apply.error.message : 'Nie udało się zmienić kosztorysu') : null;
  const notice =
    text !== '' && !closed ? (
      <Notice className="crew-quote-differs" aria-label="Obsada a kosztorys" data-feature-id="quotes.crew_sync" resetKey={round} dedupeKey={`crew-quote:${eventId}`} onClose={() => closeForNow(eventId)}>
        <span>{text}</span>
        {note && <span className="hint">{note}</span>}
        {barButtons}
        {error && <span role="alert">{error}</span>}
      </Notice>
    ) : null;

  return { ask, notice, text, buttons, dialog: null, fillCrew };
}
